import ListView from '@/components/datagrid/ListView';
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useState, useEffect } from 'react';
import useTitle from '@/hooks/useTitle';
import AddHomeWorkOutlinedIcon from '@mui/icons-material/AddHomeWorkOutlined';
import { Delete, ImportContacts } from '@mui/icons-material';
import useAppStore from '@/store/app';
import { toast } from 'react-toastify';
import ToastMessage from '@/components/toasts/ToastMessage';
import useLiveData from '@/hooks/useLiveData';
import useApiQuery from '@/hooks/useApiQuery';
import useSingleItemLoader from '@/hooks/useSingleItemLoader';
import PartyManager from '@/components/forms/party/PartyManager';
import { useInterfaceStore } from '@/store/interface';
import api from '@/services/ApiService';
import {
  useRouteLoaderData,
  useLocation,
  useParams,
  useNavigate,
} from 'react-router-dom';
import ContactSync from '@/components/dialogs/crm/ContactSync';
import { useMemo } from 'react';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import useSearchStore from '@/store/search';
import PartyQuickEntry from '@/components/forms/party/PartyQuickEntry';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';

export default function Contacts() {
  useTitle('Firmen und Kontakte');
  const {
    config,
    fixedDeploymentConfigs: { crm, tanss2weclapp },
  } = useRouteLoaderData('auth');
  const { pathname } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState([]);
  const [disableLive, setDisableLive] = useState(false);
  const { selectedItem, setSelectedItem, interfaces } = useAppStore();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [contactSyncOpen, setContactSyncOpen] = useState(false);
  const [quickEntryOpen, setQuickEntryOpen] = useState(false);
  const {
    config: crmConfig,
    setConfig,
    setWeclapp,
    setTanss,
    setOpenRoute,
    setLLM,
  } = useInterfaceStore();
  const [partyType, setPartyType] = useState('ALL');
  const { registerLocalSearch, unregisterLocalSearch } = useSearchStore();
  const [quickEntryData, setQuickEntryData] = useState(null);

  useEffect(() => {
    registerLocalSearch({
      key: 'contacts',
      label: 'Kontakte',
      onSearch: (query) => setSearchQuery(query),
      onClear: () => setSearchQuery(''),
    });

    return () => {
      unregisterLocalSearch();
    };
  }, [registerLocalSearch, unregisterLocalSearch]);
  const [searchQuery, setSearchQuery] = useState('');

  const mongoFilter = useMemo(() => {
    const filter = { $and: [] };
    if (searchQuery) {
      filter.$and.push({
        $or: [
          { firstName: { $regex: searchQuery, $options: 'i' } },
          { lastName: { $regex: searchQuery, $options: 'i' } },
          { company: { $regex: searchQuery, $options: 'i' } },
          { company2: { $regex: searchQuery, $options: 'i' } },
          { customerNumber: { $regex: searchQuery, $options: 'i' } },
          { supplierNumber: { $regex: searchQuery, $options: 'i' } },
          { phone: { $regex: searchQuery, $options: 'i' } },
          { fax: { $regex: searchQuery, $options: 'i' } },
          { mobilePhone1: { $regex: searchQuery, $options: 'i' } },
          { mobilePhone2: { $regex: searchQuery, $options: 'i' } },
          { phoneHome: { $regex: searchQuery, $options: 'i' } },
          { email: { $regex: searchQuery, $options: 'i' } },
          { 'addresses.city': { $regex: searchQuery, $options: 'i' } },
          { 'addresses.street1': { $regex: searchQuery, $options: 'i' } },
          { 'addresses.zipcode': { $regex: searchQuery, $options: 'i' } },
          { 'addresses.street2': { $regex: searchQuery, $options: 'i' } },
          { departments: { $regex: searchQuery, $options: 'i' } },
          { domains: { $regex: searchQuery, $options: 'i' } },
          { partyEmailAddresses: { $regex: searchQuery, $options: 'i' } },
          { contacts: { $regex: searchQuery, $options: 'i' } },
          { personCompany: { $regex: searchQuery, $options: 'i' } },
          { shares: true },
          { referenceNumber: { $regex: searchQuery, $options: 'i' } },
        ],
      });
    }
    if (partyType !== 'ALL') {
      filter.$and.push({ partyType: partyType });
    }
    return filter.$and.length === 0 ? {} : filter;
  }, [searchQuery, partyType]);

  const {
    isPending,
    data: parties = [],
    refetch,
  } = useApiQuery('party', mongoFilter, true);
  const { liveData } = useLiveData('party');

  const handleItemLoadError = (error, itemId) => {
    const pathWithoutId = pathname.replace(id, '').replace(/\/$/, '');
    navigate(pathWithoutId, { replace: true });
    setSelectedItem(null);
  };

  const loadSpecificParty = useSingleItemLoader('party', handleItemLoadError);

  useEffect(() => {
    const tanssInterface =
      crm?.interfaces?.tanss ??
      interfaces?.tanss ??
      tanss2weclapp?.tanssInterface ??
      null;
    const weclappInterface =
      crm?.interfaces?.weclapp ??
      interfaces?.weclapp ??
      tanss2weclapp?.weclappInterface ??
      null;
    const openRouteInterface =
      crm?.interfaces?.openRoute ??
      interfaces?.openRoute ??
      tanss2weclapp?.openRouteInterface ??
      null;
    const llmInterface =
      crm?.interfaces?.llm ??
      interfaces?.llm ??
      tanss2weclapp?.llmInterface ??
      null;
    const crmContent = crm?.crm ?? config?.crm ?? tanss2weclapp?.crm ?? null;

    setConfig(crmContent);
    setPartyType('ALL');
    setWeclapp(weclappInterface);
    setTanss(tanssInterface);
    setOpenRoute(openRouteInterface);
    setLLM(llmInterface);
  }, [interfaces]);

  const deleteParty = async (id) => {
    try {
      const { result: res } = await api
        .get(`/datasource?recordId=${id}`)
        .then(({ data }) => data);
      const dataSources = res || [];
      for (const ds of dataSources) {
        await api.delete(`/datasource/${ds._id}`);
      }
      await api.delete(`/party/${id}`);
      refetch();
    } catch (error) {
      toast.error(<ToastMessage title="Fehler" subtitle={error.message} />);
    }
  };

  const handleDelete = () => {
    selectedRows.forEach((id) => {
      if (selectedItem === id) setSelectedItem(null);
      deleteParty(id);
    });
    setSelectedRows([]);
  };

  const columns = useMemo(() => {
    const cols = [
      {
        field: 'customerNumber',
        headerName: 'Knd.-Nr.',
        flex: 0.5,
        renderCell: (params) => (
          <a>
            {params.row.type === 'PERSON'
              ? ''
              : (params.row.customerNumber ?? '')}
          </a>
        ),
      },
      {
        field: 'supplierNumber',
        headerName: 'Lf.-Nr.',
        flex: 0.5,
        renderCell: (params) => <a>{params.row.supplierNumber ?? ''}</a>,
      },
      {
        field: 'name',
        headerName: 'Name',
        align: 'center',
        flex: 0.8,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {params.row.partyType === 'ORGANIZATION'
              ? params.row.company
              : `${params.row.firstName} ${params.row.lastName}`}
          </Box>
        ),
      },
      {
        field: 'phoneNumber',
        headerName: 'Telefon',
        flex: 0.5,
        renderCell: (params) => (
          <a
            href={`tel:${params.row.phone}`}
            onClick={(e) => e.stopPropagation()}
          >
            {params.row.phone ?? ''}
          </a>
        ),
      },
      {
        field: 'email',
        headerName: 'E-Mail',
        flex: 0.7,
        renderCell: (params) => <a>{params.row.email ?? ''}</a>,
      },
      {
        field: 'type',
        headerName: 'Typ',
        flex: 0.7,
        renderCell: (params) =>
          params.row.partyType === 'ORGANIZATION'
            ? 'Firma'
            : params.row.partyType === 'PERSON'
              ? 'Person'
              : '-',
      },
    ];

    if (partyType === 'ALL') {
      cols.unshift({
        field: 'partyType',
        headerName: 'Typ',
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {params.row.partyType === 'PERSON' ? (
              params.row.customer === true ? (
                <PersonAddOutlinedIcon />
              ) : (
                <PersonOutlinedIcon />
              )
            ) : (
              <HomeWorkOutlinedIcon />
            )}
          </Box>
        ),
        flex: 0.1,
      });
    }

    return cols;
  }, [partyType]);

  const handleImport = () => {
    setContactSyncOpen(true);
  };

  const handleQuickEntrySelect = (partyData) => {
    setQuickEntryData(partyData);
    setQuickEntryOpen(false);
    setDialogOpen(true);
  };

  const handleClosePartyManager = () => {
    setDialogOpen(false);
    setQuickEntryData(null);
  };

  const actions = [];
  if (!crmConfig?.hidePartyEntryAction) {
    actions.push(
      <Tooltip key="add-org" title="Neuen Kontakt anlegen">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            color="primary"
            onClick={() => {
              setDialogOpen(true);
            }}
          >
            <AddHomeWorkOutlinedIcon />
          </IconButton>
        </Box>
      </Tooltip>,
    );
  }
  if (!crmConfig?.hideQuickPartyEntryAction) {
    actions.push(
      <Tooltip key="add-org-short" title="Schnell Erfassung">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            color="primary"
            onClick={() => {
              setQuickEntryOpen(true);
            }}
          >
            <PendingActionsOutlinedIcon />
          </IconButton>
        </Box>
      </Tooltip>,
    );
  }
  actions.push(
    <Tooltip
      key="delete"
      title={
        selectedRows.length ? 'Ausgewählte löschen' : 'Keine Zeile ausgewählt'
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          color="error"
          disabled={!selectedRows.length}
          onClick={handleDelete}
        >
          <Delete />
        </IconButton>
      </Box>
    </Tooltip>,
  );

  if (!crmConfig?.hideFullSyncAction) {
    actions.push(
      <Tooltip key="import" title="Stammdaten importieren">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={handleImport}>
            <ImportContacts />
          </IconButton>
        </Box>
      </Tooltip>,
    );
  }
  actions.push(<Box key="spacer" sx={{ flex: 1 }} />);
  actions.push(
    <ButtonGroup
      key="filter"
      variant="outlined"
      aria-label="Filter"
      size="small"
      sx={{
        width: 230,
        display: 'flex',
        '& .MuiButton-root': {
          flex: 1,
          minHeight: 30,
          paddingX: 1,
          fontSize: '0.75rem',
        },
      }}
    >
      <Button
        size="small"
        onClick={() => setPartyType('ALL')}
        variant={partyType === 'ALL' ? 'contained' : 'outlined'}
      >
        Alle
      </Button>
      <Button
        size="small"
        onClick={() => setPartyType('ORGANIZATION')}
        variant={partyType === 'ORGANIZATION' ? 'contained' : 'outlined'}
      >
        Firmen
      </Button>
      <Button
        size="small"
        onClick={() => setPartyType('PERSON')}
        variant={partyType === 'PERSON' ? 'contained' : 'outlined'}
      >
        Kontakte
      </Button>
    </ButtonGroup>,
  );

  return (
    <>
      <ListView
        key={JSON.stringify(mongoFilter)}
        rows={parties}
        columns={columns}
        actions={actions}
        loading={isPending}
        refetch={refetch}
        livedata={!disableLive ? liveData : null}
        detailComponents={[{ Component: PartyManager, title: 'Details' }]}
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRows}
        columnSearch={true}
        loadSpecificItem={loadSpecificParty}
      />
      <Dialog
        open={dialogOpen}
        onClose={handleClosePartyManager}
        fullWidth={true}
        maxWidth="md"
        sx={{ '& .MuiDialog-paper': { width: '600px' } }}
      >
        <PartyManager
          data={quickEntryData}
          handleClose={handleClosePartyManager}
        />
      </Dialog>

      <PartyQuickEntry
        open={quickEntryOpen}
        onClose={() => setQuickEntryOpen(false)}
        onSelect={handleQuickEntrySelect}
      />
      <ContactSync
        open={contactSyncOpen}
        onClose={() => setContactSyncOpen(false)}
        setDisableLive={setDisableLive}
      />
    </>
  );
}
